import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import api from "../axios.config";

interface FormattedValue<T> {
  value: T;
  formattedValue: string;
}

export interface ProductDetails {
  id: string;
  productName: string;
  manufacturerId: string;
  manufacturerName: string;
  saltInfo: string;
  imageUrl: string;
  noOfBatches: number;
  stock: FormattedValue<string>;
  mrp: FormattedValue<number>;
  ptr: FormattedValue<number>;
  prate: FormattedValue<number>;
  srate: FormattedValue<number>;
  marginPercent: FormattedValue<number>;
  movingType: { value: string; formattedValue: string };
  expiryDate: string;
  expiryType: FormattedValue<string>;
  packageForm: string;
  productMapId: string;
  age?: string;
  stockType?: { value: string; formattedValue: string };
  b2cMargin?: FormattedValue<number>;
  isPrRateToBeShown: boolean;
}

export interface AlternateProduct {
  id: string;
  productName: string;
  saltInfo: string;
  mrp: {
    value: number;
    formattedValue: string;
  };
  imageUrl: string;
  stockStatus: "IN_STOCK" | "OUT_OF_STOCK";
  expiryType: {
    value: "SHORT_EXPIRY" | "LONG_EXPIRY";
    formattedValue: string;
  } | null;
  movingType: {
    value: "MOVING" | "NON_MOVING";
    formattedValue: string;
  } | null;
}

export interface AlternateProductsResponse {
  body: AlternateProduct[];
  type: string;
}

export interface ProductDetailsResponse {
  body: ProductDetails;
  type: string;
}

export const useProductDetails = (
  productId: string,
  enabled: boolean = true
) => {
  return useQuery<ProductDetailsResponse, AxiosError>({
    queryKey: ["product-details", productId],
    queryFn: async ({ signal }) => {
      const url = `${
        process.env.REACT_APP_API_MUKUNDA_SEARCH_DETAILS
      }/${encodeURIComponent(productId)}`;
      return api.get(url, { signal }).then((r) => r.data);
    },
    enabled: enabled && !!productId,
  });
};

export const useAlternateProducts = (
  productId: string,
  enabled: boolean = true
) => {
  return useQuery<AlternateProductsResponse, AxiosError>({
    queryKey: ["alternate-products", productId],
    queryFn: async ({ signal }) => {
      const url = `${
        process.env.REACT_APP_API_MUKUNDA_SEARCH_ALTERNATE_PRODUCTS
      }/${encodeURIComponent(productId)}`;
      return api.get(url, { signal }).then((r) => r.data);
    },
    enabled: enabled && !!productId,
  });
};

export interface ProductTabDetails {
  name: string;
  manufacturer: string;
  packageType: string;
  saltInfo: string;
  packaging: string;
  stockStatus: string;
  description: string;
  howToUse: string;
  commonSideEffects: string[];
  storage: string;
}

export interface ProductTabDetailsResponse {
  body: ProductTabDetails;
  type: string;
}

export const useProductTabDetails = (
  productId: string,
  enabled: boolean = true
) => {
  return useQuery<ProductTabDetailsResponse, AxiosError>({
    queryKey: ["product-tab-details", productId],
    queryFn: async ({ signal }) => {
      const url = `${
        process.env.REACT_APP_API_MUKUNDA_SEARCH_PRODUCT_DETAILS
      }/${encodeURIComponent(productId)}`;
      return api.get(url, { signal }).then((r) => r.data);
    },
    enabled: enabled && !!productId,
  });
};

export interface AppProduct {
  id: string;
  productName: string;
  manufacturerId: string;
  manufacturerName: string;
  saltInfo: string;
  imageUrl: string;
  noOfBatches: number;
  stock: {
    value: string;
    formattedValue: string;
  };
  mrp: { value: number; formattedValue: string };
  ptr: { value: number; formattedValue: string };
  prate: { value: number; formattedValue: string };
  srate: { value: number; formattedValue: string };
  marginPercent: { value: number; formattedValue: string };
  b2cMargin: { value: number; formattedValue: string };
  movingType: {
    value: "FAST_MOVING" | "AVG_MOVING" | "SLOW_MOVING" | "NON_MOVING";
    formattedValue: string;
  };
  expiryDate: string;
  expiryType: {
    value: "LONG_EXPIRY" | "MEDIUM_EXPIRY" | "SHORT_EXPIRY" | "EXPIRED";
    formattedValue: string;
  };
  packageForm: string;
  productMapId: string;
  age: string;
  stockType: {
    value: "IN_STOCK" | "LOW_STOCK" | "OUT_OF_STOCK" | "ZERO_STOCK";
    formattedValue: string;
  };
  isPrRateToBeShown: boolean;
  batches?: number;
  movementStatus?: string;
  // Dynamic fields
  rating?: {
    value: number;
    count: number;
  };
  deliveryAvailable?: boolean;
  prescriptionRequired?: boolean;
  categories?: string[];
}

export interface SaltFilter {
  code: string;
  name: string;
}

export interface ManufacturerFilter {
  code: string;
  name: string;
}

export interface AppProductsResponse {
  body: {
    results: AppProduct[];
    totalCount: number;
    manufacturerFilters: ManufacturerFilter[];
    saltFilters: SaltFilter[];
    movementFilters?: { code: string; name: string }[];
    expiryFilters?: { code: string; name: string }[];
    stockFilters?: { code: string; name: string }[];
  };
  type: string;
}

// New interface for the provided data structure
export interface NewSearchResponse {
  body: {
    results: AppProduct[];
    totalCount: number;
    manufacturerFilters: ManufacturerFilter[];
    saltFilters: SaltFilter[];
    movementFilters: MovementFilter[];
    expiryFilters: ExpiryFilter[];
    stockFilters: StockFilter[];
    selectedManufacturers: string[];
    selectedSalts: string[];
    selectedMovementStatus: string[];
    selectedExpiryType: string[];
    selectedStockType: string[];
  };
  type: string;
}

export interface MovementFilter {
  code: "FAST_MOVING" | "SLOW_MOVING" | "NON_MOVING" | "AVG_MOVING";
  name: string;
}

export interface ExpiryFilter {
  code: "EXPIRED" | "SHORT_EXPIRY" | "MEDIUM_EXPIRY" | "LONG_EXPIRY";
  name: string;
}

export interface StockFilter {
  code: "IN_STOCK" | "LOW_STOCK" | "OUT_OF_STOCK" | "ZERO_STOCK";
  name: string;
}

export type AppProductsParams = {
  searchKey?: number;
  search?: string;
  page?: number;
  pageSize?: number;
  manufacturers?: string;
  saltInfo?: string;
  saltInfoId?: string;
  stockType?: string;
  movementStatus?: string;
  expiryType?: string;
};

export const useAppProducts = (
  params: AppProductsParams = {},
  enabled: boolean = true
) => {
  return useQuery<AppProductsResponse, AxiosError>({
    queryKey: ["app-products", params],
    queryFn: async ({ signal }) => {
      const url = new URL(process.env.REACT_APP_API_MUKUNDA_SEARCH_RESULTS!);
      // Append all params as query parameters
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
      return api.get(url.toString(), { signal }).then((r) => r.data);
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

// New API function for the provided data structure
export const useNewProductSearch = (
  search: string,
  enabled: boolean = true
) => {
  return useQuery<NewSearchResponse, AxiosError>({
    queryKey: ["new-product-search", search],
    queryFn: async () => {
      const response = await api.get<NewSearchResponse>(
        `${
          process.env.REACT_APP_API_MUKUNDA_SEARCH_RESULTS
        }?search=${encodeURIComponent(search)}&page=0&pageSize=20`
      );
      return response.data;
    },
    enabled: enabled && !!search,
  });
};

export interface SaltInfoData {
  id: string;
  name: string;
  totalCount: number;
  description: string;
}

export interface MukundaProductData {
  id: string;
  productName: string;
  manufacturerId: string;
  manufacturerName: string;
  saltInfo: string;
  imageUrl: string;
  noOfBatches: number;
  stock: FormattedValue<string>;
  sRate: FormattedValue<number>;
  pRate: FormattedValue<number>;
  mrp: FormattedValue<number>;
  ptr: FormattedValue<number>;
  marginPercent: FormattedValue<number>;
  b2cMargin: FormattedValue<number>;
  movingType: FormattedValue<string>;
  expiryDate: string;
  expiryType: FormattedValue<string>;
  packageForm: string;
  productMapId: string;
  age: string;
  stockType: FormattedValue<string>;
}

export interface MukundaSuggestorData {
  saltInfoInfo: SaltInfoData[];
  mukundaProductData: MukundaProductData[];
}

export interface MukundaAutocompleteResponse {
  body: MukundaSuggestorData;
  type: string;
}

export const useMukundaAutocomplete = (
  suggest: string,
  enabled: boolean = true
) => {
  return useQuery<MukundaAutocompleteResponse, AxiosError>({
    queryKey: ["mukunda-autocomplete", suggest],
    queryFn: async ({ signal }) => {
      const url = new URL(process.env.REACT_APP_API_MUKUNDA_SEARCH!);
      url.searchParams.append("suggest", suggest);
      return api.get(url.toString(), { signal }).then((r) => r.data);
    },
    enabled: enabled && !!suggest && suggest.length > 0,
  });
};
